/**
 * SSO Popup Helper
 * Handles SSO authentication via popup window to bypass iframe X-Frame-Options restrictions
 *
 * Flow:
 * 1. Parent iframe opens popup with OAuth URL
 * 2. Popup completes OAuth flow and receives callback with tokens
 * 3. Popup signals parent that auth is complete
 * 4. Parent reloads to pick up authenticated session
 */

import { Auth, Hub } from 'aws-amplify';

import { awsmobile } from 'aws-exports';

// Message types for postMessage communication
const SSO_AUTH_ERROR_MESSAGE = 'SSO_AUTH_ERROR';
const SSO_AUTH_TOKENS_MESSAGE = 'SSO_AUTH_TOKENS';

// Popup window dimensions
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;

// Timeout for popup operations (ms)
const AMPLIFY_PROCESSING_DELAY = 1000;
const AMPLIFY_SYNC_DELAY = 500;
const POPUP_MESSAGE_SEND_DELAY = 500;
const POPUP_CLOSE_DELAY = 100;
const POPUP_FORCE_CLOSE_TIMEOUT = 10000;

// Window name for SSO popup identification
const SSO_POPUP_WINDOW_NAME = 'planera-sso-popup';

// Hub unsubscribe function for cleanup
let hubUnsubscribe: (() => void) | null = null;

// User-facing error messages (exported for use in App.tsx)
export const SSO_ERROR_MESSAGES = {
  FAILED: 'SSO login failed. Please try again.',
  CANCELLED: 'Login cancelled. Please try again.',
  POPUP_BLOCKED: 'Please allow popups and try again.',
  SESSION_VERIFICATION_FAILED: 'Authentication completed but session could not be verified.'
} as const;

/**
 * Check if the app is running inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions, we're definitely in an iframe
    return true;
  }
}

/**
 * Extract all Cognito authentication tokens from localStorage
 * These tokens need to be transferred from popup to parent iframe
 */
function extractCognitoTokens(): Record<string, string> {
  const tokens: Record<string, string> = {};
  const clientId = awsmobile.aws_user_pools_web_client_id;
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;

  // Iterate through all localStorage items
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const value = localStorage.getItem(key);
      if (value) {
        tokens[key] = value;
      }
    }
  }

  logger.info('Extracted Cognito tokens from localStorage', { extra: { tokenKeys: Object.keys(tokens) } });
  return tokens;
}

/**
 * Build the OAuth URL for federated sign-in
 */
function buildOAuthUrl(customProvider: string): string {
  const { oauth } = awsmobile;
  const domain = oauth.domain;
  const redirectUri = encodeURIComponent(oauth.redirectSignIn);
  const responseType = oauth.responseType || 'code';
  const clientId = awsmobile.aws_user_pools_web_client_id;
  const scope = encodeURIComponent(oauth.scope.join(' '));

  // Build the OAuth URL for the custom provider (SAML)
  return `https://${domain}/oauth2/authorize?identity_provider=${customProvider}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&scope=${scope}`;
}

/**
 * Handle OAuth callback in the popup window
 * This function should be called on the redirect URL page
 * Internal function - only called by initOAuthCallbackHandler
 */
async function handleOAuthCallbackInPopup(): Promise<void> {
  // Check if this is a popup window (has opener)
  if (!window.opener) {
    return;
  }

  logger.info('Popup window detected, checking for OAuth callback...');

  try {
    // Check if we have OAuth response in the URL
    const hash = window.location.hash.substring(1);
    const search = window.location.search.substring(1);
    const params = new URLSearchParams(hash || search);

    logger.info('Popup URL params', { extra: { hash, search } });

    // Check for error in OAuth response
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      logger.error('OAuth error in popup', {
        error: new Error(errorDescription || error),
        extra: { errorDescription },
        rum: {
          context: {
            feature: 'sso-popup',
            action: 'oauth-callback-error'
          }
        }
      });
      window.opener.postMessage(
        {
          type: SSO_AUTH_ERROR_MESSAGE,
          error: errorDescription || error
        },
        window.location.origin
      );
      setTimeout(() => {
        if (hubUnsubscribe) hubUnsubscribe();
        window.close();
      }, POPUP_CLOSE_DELAY);
      return;
    }

    // Check for token response (implicit flow) or code response (authorization code flow)
    const hasToken = params.has('access_token') || params.has('id_token');
    const hasCode = params.has('code');

    if (hasToken || hasCode) {
      logger.info('OAuth callback detected in popup, processing tokens...');

      // Give Amplify time to process the OAuth callback from the URL
      // Amplify's Hub listener should automatically handle this
      // Note: setTimeout is required here as there's no event-based way to detect when Amplify completes OAuth processing
      await new Promise((resolve) => setTimeout(resolve, AMPLIFY_PROCESSING_DELAY));

      // Try to verify authentication
      try {
        await Auth.currentAuthenticatedUser();
        logger.info('Authentication successful in popup');
      } catch (authError) {
        // Even if this fails, continue - the session might be set in parent window
        logger.warn('Could not verify auth in popup, but continuing', { extra: { authError } });
      }

      logger.info('Notifying parent window of success with tokens...');

      // Extract all Cognito tokens from localStorage
      const tokens = extractCognitoTokens();

      // Notify the opener window that authentication was successful and send tokens
      window.opener.postMessage(
        {
          type: SSO_AUTH_TOKENS_MESSAGE,
          tokens
        },
        window.location.origin
      );

      // Close popup after a brief delay to ensure postMessage is sent before window closes
      // Note: setTimeout is required to ensure postMessage completes before window.close()
      setTimeout(() => {
        logger.info('Closing popup window');
        if (hubUnsubscribe) hubUnsubscribe();
        window.close();
      }, POPUP_MESSAGE_SEND_DELAY);
    } else {
      // No OAuth params found - this might be the initial page load before redirect
      logger.info('No OAuth params in popup URL, waiting for OAuth redirect...');
    }
  } catch (error) {
    logger.error('Error handling OAuth callback in popup', {
      error: error,
      rum: {
        context: {
          feature: 'sso-popup',
          action: 'handle-oauth-callback'
        }
      }
    });
    if (window.opener) {
      window.opener.postMessage(
        {
          type: SSO_AUTH_ERROR_MESSAGE,
          error: 'Failed to process authentication'
        },
        window.location.origin
      );
    }
    setTimeout(() => {
      if (hubUnsubscribe) hubUnsubscribe();
      window.close();
    }, POPUP_MESSAGE_SEND_DELAY);
  }
}

/**
 * Open SSO login in a popup window
 * @param customProvider The identity provider name (e.g., domain from email)
 * @returns Promise that resolves when authentication is complete
 */
export function signInWithPopup(customProvider: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const oauthUrl = buildOAuthUrl(customProvider);

    // Calculate popup dimensions and position
    const left = window.screen.width / 2 - POPUP_WIDTH / 2;
    const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

    logger.info('Opening SSO popup window', { extra: { provider: customProvider } });

    // Open popup window with specific name for identification
    const popup = window.open(oauthUrl, SSO_POPUP_WINDOW_NAME, `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`);

    if (!popup) {
      logger.warn('Failed to open popup - likely blocked', {
        extra: {
          feature: 'sso-popup',
          action: 'open-popup-blocked'
        }
      });
      reject(new Error(SSO_ERROR_MESSAGES.POPUP_BLOCKED));
      return;
    }

    let pollTimer: NodeJS.Timeout | null = null;

    // Listener for messages from the popup
    const messageListener = async (event: MessageEvent) => {
      // Verify the message is from the expected origin
      if (event.origin !== window.location.origin) {
        logger.warn('Received message from unexpected origin', { extra: { origin: event.origin } });
        return;
      }

      if (event.data.type === SSO_AUTH_TOKENS_MESSAGE) {
        logger.info('Received auth tokens from popup');

        // Clean up
        window.removeEventListener('message', messageListener);
        if (pollTimer) clearInterval(pollTimer);

        // Try to close the popup from parent side as backup
        try {
          if (popup && !popup.closed) {
            popup.close();
            logger.info('Closed popup from parent window');
          }
        } catch (err) {
          logger.warn('Could not close popup from parent', { extra: { error: err } });
        }

        try {
          // Write all tokens to parent window's localStorage
          const tokens = event.data.tokens as Record<string, string>;
          if (tokens && typeof tokens === 'object') {
            logger.info('Writing tokens to parent localStorage', { extra: { tokenKeys: Object.keys(tokens) } });
            Object.entries(tokens).forEach(([key, value]) => {
              localStorage.setItem(key, value);
            });
          } else {
            logger.warn('No tokens received from popup');
          }

          // Give Amplify a moment to recognize the new tokens
          // Note: setTimeout required as Amplify needs time to process tokens in localStorage
          await new Promise((r) => setTimeout(r, AMPLIFY_SYNC_DELAY));

          // Verify authentication is complete
          await Auth.currentAuthenticatedUser();
          logger.info('Authentication verified in parent window');
          resolve();
        } catch (error) {
          logger.error('Error verifying authentication after popup', {
            error: error,
            rum: {
              context: {
                feature: 'sso-popup',
                action: 'verify-authentication-after-popup'
              }
            }
          });
          reject(new Error(SSO_ERROR_MESSAGES.SESSION_VERIFICATION_FAILED));
        }
      } else if (event.data.type === SSO_AUTH_ERROR_MESSAGE) {
        logger.error('Received error message from popup', {
          error: new Error(event.data.error || SSO_ERROR_MESSAGES.FAILED),
          rum: {
            context: {
              feature: 'sso-popup',
              action: 'received-popup-error-message'
            }
          }
        });
        window.removeEventListener('message', messageListener);
        if (pollTimer) clearInterval(pollTimer);

        // Try to close the popup from parent side
        try {
          if (popup && !popup.closed) {
            popup.close();
            logger.info('Closed popup after error from parent window');
          }
        } catch (err) {
          logger.warn('Could not close popup from parent', { extra: { error: err } });
        }

        reject(new Error(event.data.error || SSO_ERROR_MESSAGES.FAILED));
      }
    };

    // Listen for messages from the popup
    window.addEventListener('message', messageListener);

    // Poll to detect if popup was closed manually
    // Note: Polling with setInterval is necessary as there's no event for popup window closure
    pollTimer = setInterval(() => {
      if (popup.closed) {
        logger.warn('Popup was closed manually by user');
        if (pollTimer) clearInterval(pollTimer);
        window.removeEventListener('message', messageListener);
        reject(new Error(SSO_ERROR_MESSAGES.CANCELLED));
      }
    }, 500);
  });
}

/**
 * Initialize OAuth callback handler
 * Call this in your app's main component or index file
 */
export function initOAuthCallbackHandler(): void {
  // Only handle callback if we're in an SSO popup window (not any window opened via window.open)
  const isSsoPopup = window.opener && window.name === SSO_POPUP_WINDOW_NAME;

  if (isSsoPopup) {
    logger.info('Initializing OAuth callback handler in popup');

    // Set up Hub listener to detect auth success
    hubUnsubscribe = Hub.listen('auth', (data) => {
      if (data.payload.event === 'signIn') {
        logger.info('SignIn event detected in popup, sending tokens to parent...');

        // Extract all Cognito tokens from localStorage
        const tokens = extractCognitoTokens();

        // Notify parent of success with tokens
        try {
          window.opener.postMessage({ type: SSO_AUTH_TOKENS_MESSAGE, tokens }, window.location.origin);
        } catch (err) {
          logger.error('Failed to notify parent', {
            error: err,
            rum: {
              context: {
                feature: 'sso-popup',
                action: 'notify-parent'
              }
            }
          });
        }

        // Close popup after delay to ensure postMessage is delivered
        // Note: setTimeout required to ensure postMessage completes before window closes
        setTimeout(() => {
          if (hubUnsubscribe) hubUnsubscribe();
          window.close();
        }, POPUP_MESSAGE_SEND_DELAY);
      }
    });

    // Also run the URL-based handler
    void handleOAuthCallbackInPopup();

    // Force close popup after timeout as a safety measure to prevent orphaned windows
    // Note: setTimeout required as a failsafe mechanism with no alternative
    setTimeout(() => {
      if (window.opener && !window.closed) {
        logger.warn('Force closing popup after timeout');
        if (hubUnsubscribe) hubUnsubscribe();
        window.close();
      }
    }, POPUP_FORCE_CLOSE_TIMEOUT);
  }
}
